import * as React from "react"
import { cn } from "@/lib/utils"

export interface StatusPillProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'paid' | 'overdue' | 'due-today' | 'due-soon' | 'future' | 'default' | 'pending' | 'paid-standard' | 'overdue-standard' | 'cancelled'
  children: React.ReactNode
  size?: 'sm' | 'md' | 'auto'
}

const statusVariants = {
  // Legacy variants (for backward compatibility)
  paid: 'bg-emerald-100 text-emerald-800',
  overdue: 'bg-red-100 text-red-800',
  'due-today': 'bg-amber-100 text-amber-800',
  'due-soon': 'bg-blue-100 text-blue-800',
  future: 'bg-muted/50 text-muted-foreground',
  default: 'bg-muted/50 text-muted-foreground',
  // New standard variants (matching TransactionStatusSelector colors)
  pending: 'bg-finance-amber/20 text-finance-amber font-bold border-finance-amber/40 shadow-sm',
  'paid-standard': 'bg-finance-green/10 text-finance-green border-finance-green/30',
  'overdue-standard': 'bg-finance-red/10 text-finance-red border-finance-red/30',
  cancelled: 'bg-muted/50 text-muted-foreground border-muted-foreground/30'
}

const sizeVariants = {
  sm: 'w-16',
  md: 'w-20',
  auto: 'w-auto'
}

export function StatusPill({
  variant = 'default',
  size = 'md',
  className,
  children,
  ...props
}: StatusPillProps) {
  return (
    <span
      className={cn(
        // Fixed width container with flex centering
        'inline-flex items-center justify-center',
        // Fixed size (only apply if not auto)
        size !== 'auto' && sizeVariants[size],
        // Pill styling - use font-medium as default, but allow variant to override
        'px-1.5 py-1 rounded-full text-xs whitespace-nowrap',
        variant !== 'pending' && 'font-medium',
        // Status-based colors (only apply if no custom colors in className)
        statusVariants[variant],
        className
      )}
      {...props}
    >
      {children}
    </span>
  )
}